import React, { useEffect, useState, useCallback, useRef } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { usePharmacy } from '../context/PharmacyContext';

export default function BillingPage() {
  // ---------- RESPONSIVE & STATE ----------
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ---------- CORE DATA ----------
  const [activeTab, setActiveTab] = useState('billing');
  const [medicines, setMedicines] = useState([]);
  const [sales, setSales] = useState([]);
  const [parties, setParties] = useState([]);

  // pharmacyDetails from shared context — no extra API call needed
  const { pharmacyDetails } = usePharmacy();

  // ---------- BILLING FORM STATE ----------
  const [billItems, setBillItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPartyId, setSelectedPartyId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [paymentType, setPaymentType] = useState('CASH');
  const [paidAmount, setPaidAmount] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [taxPercent, setTaxPercent] = useState('0');
  const [notes, setNotes] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [loading, setLoading] = useState(false);

  // ---------- Promo Coupons & Held Bills states ----------
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscountPercent, setCouponDiscountPercent] = useState(0);

  const [heldBills, setHeldBills] = useState(() => {
    try {
      const saved = localStorage.getItem('heldBills');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showHeldModal, setShowHeldModal] = useState(false);

  const applyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (code === 'WELCOME10') {
      setCouponDiscountPercent(10);
      setCouponApplied(true);
      toast.success('Coupon WELCOME10 applied! 10% discount added.');
    } else if (code === 'FESTIVAL15') {
      setCouponDiscountPercent(15);
      setCouponApplied(true);
      toast.success('Coupon FESTIVAL15 applied! 15% discount added.');
    } else if (code === 'SAVE20') {
      setCouponDiscountPercent(20);
      setCouponApplied(true);
      toast.success('Coupon SAVE20 applied! 20% discount added.');
    } else {
      toast.error('Invalid coupon code.');
      setCouponApplied(false);
      setCouponDiscountPercent(0);
    }
  };

  const holdBill = () => {
    if (billItems.length === 0) return toast.error('Cannot hold an empty cart');
    const tag = prompt('Enter a note/tag to identify this held bill:', `Bill #${billNumber.slice(-4)}`);
    if (tag === null) return;

    const newHeld = {
      id: Date.now().toString(),
      tag: tag || `Draft - ${new Date().toLocaleTimeString()}`,
      billItems,
      selectedPartyId,
      customerName,
      patientName,
      patientAge,
      patientGender,
      doctorName,
      discountAmount,
      discountPercent,
      taxPercent,
      notes,
      couponCode,
      couponApplied,
      couponDiscountPercent,
      createdAt: new Date().toISOString()
    };

    const updated = [...heldBills, newHeld];
    setHeldBills(updated);
    localStorage.setItem('heldBills', JSON.stringify(updated));
    toast.success('Invoice placed on hold.');

    setBillItems([]);
    setCustomerName('');
    setSelectedPartyId('');
    setDiscountAmount('');
    setDiscountPercent('');
    setTaxPercent('0');
    setPatientName('');
    setPatientAge('');
    setDoctorName('');
    setNotes('');
    setCouponCode('');
    setCouponApplied(false);
    setCouponDiscountPercent(0);
    generateBillNumber();
  };

  const resumeBill = (held) => {
    setBillItems(held.billItems);
    setSelectedPartyId(held.selectedPartyId || '');
    setCustomerName(held.customerName || '');
    setPatientName(held.patientName || '');
    setPatientAge(held.patientAge || '');
    setPatientGender(held.patientGender || 'Male');
    setDoctorName(held.doctorName || '');
    setDiscountAmount(held.discountAmount || '');
    setDiscountPercent(held.discountPercent || '');
    setTaxPercent(held.taxPercent || '0');
    setNotes(held.notes || '');
    setCouponCode(held.couponCode || '');
    setCouponApplied(held.couponApplied || false);
    setCouponDiscountPercent(held.couponDiscountPercent || 0);

    const updated = heldBills.filter(hb => hb.id !== held.id);
    setHeldBills(updated);
    localStorage.setItem('heldBills', JSON.stringify(updated));
    setShowHeldModal(false);
    toast.success(`Resumed held bill: ${held.tag}`);
  };

  const deleteHeldBill = (id) => {
    const updated = heldBills.filter(hb => hb.id !== id);
    setHeldBills(updated);
    localStorage.setItem('heldBills', JSON.stringify(updated));
    toast.info('Held bill deleted.');
  };

  // ---------- Patient & Doctor details ----------
  const [patientName, setPatientName] = useState('');
  const [patientAge, setPatientAge] = useState('');
  const [patientGender, setPatientGender] = useState('Male');
  const [doctorName, setDoctorName] = useState('');

  // ---------- LAST COMPLETED SALE (FOR PRINT) ----------
  const [lastCompletedSale, setLastCompletedSale] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // ---------- RETURN FORM STATE ----------
  const [searchBill, setSearchBill] = useState('');
  const [selectedSaleForReturn, setSelectedSaleForReturn] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returnReason, setReturnReason] = useState('');

  // ---------- MODAL & BATCH STATE ----------
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [selectedMedForQty, setSelectedMedForQty] = useState(null);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [qtyInputValue, setQtyInputValue] = useState(1);
  const qtyInputRef = useRef(null);
  const searchInputRef = useRef(null);

  const upiId = 'pharmacy@upi';

  // ---------- UTILS ----------
  const fmt = (num) => (Number.isFinite(Number(num)) ? Number(Number(num)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');
  const round = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

  const getBatchExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const exp = new Date(expiryDate);
    const now = new Date();
    const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return { status: 'EXPIRED', label: 'EXPIRED ❌', color: '#991b1b', bg: '#fef5f5' };
    if (diffDays <= 30) return { status: 'WARNING', label: `Expiring in ${diffDays}d ⚠️`, color: '#c2410c', bg: '#fff7ed' };
    return null;
  };

  // ---------- FETCH DATA ----------
  const fetchMedicinesAndParties = useCallback(async () => {
    try {
      const [medRes, partyRes] = await Promise.all([
        API.get('/medicines'),
        API.get('/parties')
      ]);
      setMedicines(medRes.data || []);
      setParties(partyRes.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchSales = useCallback(async () => {
    try {
      const salesRes = await API.get('/sales');
      setSales(salesRes.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);


  // Load medicines + parties immediately on mount (fast — needed for billing)
  useEffect(() => {
    fetchMedicinesAndParties();
    generateBillNumber();
  }, [fetchMedicinesAndParties]);

  // Load sales history lazily — only when user opens the Returns tab
  useEffect(() => {
    if (activeTab === 'returns' && sales.length === 0) {
      fetchSales();
    }
  }, [activeTab]);

  const generateBillNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setBillNumber(`INV-${timestamp}${random}`);
  };

  useEffect(() => {
    if (showQtyModal && qtyInputRef.current) setTimeout(() => qtyInputRef.current.focus(), 50);
  }, [showQtyModal]);

  // Global POS Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2' || (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA')) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'F4') {
        e.preventDefault();
        if (billItems.length > 0) holdBill();
      } else if (e.key === 'F8' || (e.ctrlKey && e.key === 'Enter')) {
        e.preventDefault();
        if (billItems.length > 0 && !loading) submitSale();
      } else if (e.key === 'Escape') {
        if (showQtyModal) setShowQtyModal(false);
        if (showSuccessModal) setShowSuccessModal(false);
        if (showHeldModal) setShowHeldModal(false);
        if (searchTerm) setSearchTerm(''); // Close search dropdown on ESC
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [billItems, loading, showQtyModal, showSuccessModal, showHeldModal, searchTerm]);

  // ---------- PRINT FUNCTION ----------
  const handlePrint = (sale) => {
    const printWindow = window.open('', '_blank');
    const itemsList = ((sale.items || sale.products) || [])
      .map(i => `
        <tr>
          <td>
            <div style="font-weight:600;">${i.name || '-'}</div>
            ${i.batchNumber ? `<div style="font-size: 8px; color: #64748b;">Batch: ${i.batchNumber}</div>` : ''}
          </td>
          <td style="text-align: center;">${i.quantity || 0} ${i.quantityUnit || ''}</td>
          <td style="text-align: right;">Rs ${(i.price || 0).toFixed(2)}</td>
          <td style="text-align: right; font-weight: 600;">Rs ${((i.price || 0) * (i.quantity || 0)).toFixed(2)}</td>
        </tr>
      `)
      .join('');

    const subtotal = (sale.subtotal || 0);
    const balance = (sale.totalAmount || 0) - (sale.paidAmount || 0);

    let contactInfo = '';
    if (pharmacyDetails.phone) contactInfo += `Phone: ${pharmacyDetails.phone}`;
    if (pharmacyDetails.email) contactInfo += ` | Email: ${pharmacyDetails.email}`;

    let patientSection = '';
    if (sale.patientName || sale.doctorName) {
      patientSection = `
        <div class="info-box" style="grid-column: span 2; margin-top: 5px;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 5px;">
            ${sale.patientName ? `<div><span class="info-label">Patient:</span> <span style="font-weight:700;">${sale.patientName} ${sale.patientAge ? `(${sale.patientAge} Y/O)` : ''}</span></div>` : ''}
            ${sale.doctorName ? `<div><span class="info-label">Ref. Doctor:</span> <span style="font-weight:700;">Dr. ${sale.doctorName}</span></div>` : ''}
          </div>
        </div>
      `;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${sale.billNumber}</title>
        <style>
          @page { size: auto; margin: 5mm; } 
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Helvetica', 'Arial', sans-serif; 
            padding: 10px; 
            color: #1a202c;
            font-size: 11px;
            line-height: 1.3;
            max-width: 100%;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #1880c2; 
            padding-bottom: 5px; 
            margin-bottom: 10px; 
          }
          .header h1 { 
            margin: 0; 
            color: #1880c2; 
            font-size: 20px;
            font-weight: 800;
            text-transform: uppercase;
          }
          .header p { margin: 1px 0; color: #4a5568; font-size: 10px; }
          .header .pan { font-weight: 700; color: #1a202c; }
          .invoice-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin-bottom: 10px;
          }
          .info-box {
            background: #f4f9fd;
            padding: 8px;
            border-radius: 4px;
            border: 1px solid #e0f2fe;
          }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 2px; }
          .info-label { font-size: 9px; color: #718096; font-weight: 600; text-transform: uppercase; }
          .info-val { font-size: 11px; font-weight: 700; color: #1a202c; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
          thead tr { background: #1880c2; }
          th { 
            background: #1880c2; color: white; 
            padding: 6px; 
            text-align: left; 
            font-weight: 600; font-size: 10px; text-transform: uppercase;
          }
          td { 
            padding: 6px; 
            border-bottom: 1px solid #e1e8ed; font-size: 11px; 
          }
          tbody tr:last-child td { border-bottom: none; }
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-top: 5px;
            margin-bottom: 15px;
          }
          .totals-table { width: 220px; border-collapse: collapse; margin-bottom: 0; }
          .totals-table td { padding: 3px 0; text-align: right; border: none; }
          .t-label { color: #4a5568; font-size: 10px; font-weight: 600; padding-right: 10px; }
          .t-val { font-size: 11px; font-weight: 700; color: #1a202c; }
          .grand-total { border-top: 1px solid #cbd5e1 !important; padding-top: 5px !important; margin-top: 2px; }
          .grand-total .t-label { color: #1880c2; font-size: 12px; }
          .grand-total .t-val { color: #1880c2; font-size: 14px; }
          .policy-container {
            border-top: 1px dashed #cbd5e1;
            padding-top: 10px;
            page-break-inside: avoid;
          }
          .policy-title {
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            color: #4a5568;
            margin-bottom: 5px;
            text-align: center;
          }
          .policy-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .policy-item h4 {
            font-size: 9px;
            font-weight: 700;
            margin-bottom: 2px;
            color: #1a202c;
          }
          .policy-item ul {
            padding-left: 12px;
            margin: 0;
          }
          .policy-item li {
            font-size: 8px;
            color: #718096;
            margin-bottom: 1px;
          }
          .footer { 
            margin-top: 15px; 
            text-align: center; 
            color: #718096; 
            font-size: 9px; 
            border-top: 1px solid #e1e8ed; 
            padding-top: 5px; 
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${pharmacyDetails.name}</h1>
          ${pharmacyDetails.address ? `<p>${pharmacyDetails.address}</p>` : ''}
          ${contactInfo ? `<p>${contactInfo}</p>` : ''}
          ${pharmacyDetails.pan ? `<p class="pan">PAN: ${pharmacyDetails.pan}</p>` : ''}
        </div>
        <div class="invoice-grid">
          <div class="info-box">
            <div class="info-row"><span class="info-label">Invoice No</span><span class="info-val">${sale.billNumber || 'N/A'}</span></div>
            <div class="info-row"><span class="info-label">Date</span><span class="info-val">${new Date(sale.createdAt || Date.now()).toLocaleDateString('en-IN')}</span></div>
            <div class="info-row"><span class="info-label">Payment</span><span class="info-val">${sale.paymentType}</span></div>
          </div>
          <div class="info-box">
            <div class="info-row"><span class="info-label">Customer</span><span class="info-val">${sale.partyName || 'Walk-in Customer'}</span></div>
          </div>
          ${patientSection}
        </div>
        <table>
          <thead>
            <tr>
              <th width="50%">Item</th>
              <th width="15%" style="text-align: center;">Qty</th>
              <th width="15%" style="text-align: right;">Price</th>
              <th width="20%" style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>${itemsList}</tbody>
        </table>
        <div class="totals-section">
          <table class="totals-table">
            <tr><td class="t-label">Subtotal</td><td class="t-val">Rs ${subtotal.toFixed(2)}</td></tr>
            ${sale.discount > 0 ? `<tr><td class="t-label">Discount</td><td class="t-val" style="color:#991b1b">- Rs ${(sale.discount || 0).toFixed(2)}</td></tr>` : ''}
            ${sale.tax > 0 ? `<tr><td class="t-label">Tax (${sale.taxPercent}%)</td><td class="t-val">Rs ${(sale.tax || 0).toFixed(2)}</td></tr>` : ''}
            <tr class="grand-total"><td class="t-label">Grand Total</td><td class="t-val">Rs ${(sale.totalAmount || 0).toFixed(2)}</td></tr>
            <tr><td class="t-label">Paid</td><td class="t-val">Rs ${(sale.paidAmount || 0).toFixed(2)}</td></tr>
            ${Math.abs(balance) > 0.01 ? `<tr><td class="t-label">${balance > 0 ? 'Due Amount' : 'Change'}</td><td class="t-val" style="color: ${balance > 0 ? '#991b1b' : '#16a34a'};">Rs ${Math.abs(balance).toFixed(2)}</td></tr>` : ''}
          </table>
        </div>
        <div class="policy-container">
          <div class="policy-title">Pharmacy Return Policy</div>
          <div class="policy-grid">
            <div class="policy-item">
              <h4>1. Prescription Medicines</h4>
              <ul>
                <li>Cannot be returned/exchanged once sold.</li>
                <li>No returns due to safety & legal reasons.</li>
              </ul>
            </div>
            <div class="policy-item">
              <h4>2. OTC Medicines</h4>
              <ul>
                <li>Return within 7 days with receipt.</li>
                <li>Must be unused, unopened & sealed.</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="footer">
          <p>Thank you for your business! | Valid without signature</p>
          <p>Printed: ${new Date().toLocaleString('en-IN')}</p>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const handlePrintPreview = () => {
    if (billItems.length === 0) return;
    const mockSale = {
      billNumber,
      partyName: customerName || 'Walk-in Customer',
      items: billItems.map(i => ({
        name: i.name,
        quantity: i.quantity,
        quantityUnit: i.quantityUnit,
        price: i.pricePerUnit,
        batchNumber: i.batchNumber
      })),
      subtotal,
      discount: parseFloat(discountAmount) || 0,
      taxPercent: parseFloat(taxPercent) || 0,
      tax: taxVal,
      totalAmount: grandTotal,
      paidAmount: parseFloat(paidAmount) || 0,
      paymentType,
      patientName,
      patientAge,
      doctorName,
      createdAt: new Date()
    };
    handlePrint(mockSale);
  };

  const calculatePrice = (entity, category, packaging) => {
    if (category === 'Tablet' || category === 'Capsule') {
      const units = packaging?.unitsPerStrip || 10;
      return round(entity.sellingPricePerUnit / units);
    }
    return round(entity.sellingPricePerUnit);
  };

  // ---------- STOCKS & BATCH HANDLERS ----------
  const initiateAdd = async (med) => {
    if (med.quantity <= 0) return toast.error('Out of stock');
    setSelectedMedForQty(med);
    setQtyInputValue(1);

    try {
      const res = await API.get(`/medicines/${med._id}/batches?active=true`);
      const batches = res.data || [];
      setAvailableBatches(batches);
      if (batches.length > 0) {
        setSelectedBatch(batches[0]);
      } else {
        setSelectedBatch(null);
      }
      setShowQtyModal(true);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load batches');
    }
  };

  const confirmAdd = () => {
    const qty = parseInt(qtyInputValue);
    if (!qty || qty <= 0) return toast.error('Invalid quantity');

    const maxQty = selectedBatch ? selectedBatch.quantity : selectedMedForQty.quantity;
    if (qty > maxQty) return toast.error(`Exceeds available stock (${maxQty} in selected batch)`);

    const selectedPrice = selectedBatch
      ? calculatePrice(selectedBatch, selectedMedForQty.category, selectedMedForQty.packaging)
      : calculatePrice(selectedMedForQty, selectedMedForQty.category, selectedMedForQty.packaging);

    const batchNo = selectedBatch ? selectedBatch.batchNumber : (selectedMedForQty.batchNumber || 'DEFAULT');
    const batchId = selectedBatch ? selectedBatch._id : null;

    const existingIndex = billItems.findIndex(i => i.medicineId === selectedMedForQty._id && i.batchNumber === batchNo);

    if (existingIndex > -1) {
      setBillItems(prev => prev.map((item, idx) =>
        idx === existingIndex
          ? { ...item, quantity: Math.min(item.quantity + qty, maxQty) }
          : item
      ));
    } else {
      setBillItems([...billItems, {
        medicineId: selectedMedForQty._id,
        name: selectedMedForQty.name,
        quantityUnit: selectedMedForQty.quantityUnit || 'units',
        pricePerUnit: selectedPrice,
        quantity: qty,
        batchNumber: batchNo,
        batchId: batchId,
        maxAvailable: maxQty,
        prescriptionRequired: selectedMedForQty.prescription
      }]);
    }

    setShowQtyModal(false);
    setSelectedMedForQty(null);
    setSelectedBatch(null);
    setAvailableBatches([]);
    setSearchTerm('');
  };

  const removeItem = (id, batchNo) => {
    setBillItems(prev => prev.filter(i => !(i.medicineId === id && i.batchNumber === batchNo)));
  };

  const updateQty = (id, batchNo, rawVal) => {
    const idx = billItems.findIndex(i => i.medicineId === id && i.batchNumber === batchNo);
    if (idx === -1) return;

    const item = billItems[idx];
    if (rawVal === '' || rawVal === undefined || rawVal === null) {
      setBillItems(prev => prev.map((i, index) => index === idx ? { ...i, quantity: '' } : i));
      return;
    }

    const parsed = parseInt(rawVal, 10);
    if (isNaN(parsed) || parsed <= 0) {
      setBillItems(prev => prev.map((i, index) => index === idx ? { ...i, quantity: '' } : i));
      return;
    }

    const finalQty = Math.min(parsed, item.maxAvailable);
    setBillItems(prev => prev.map((i, index) => index === idx ? { ...i, quantity: finalQty } : i));
  };

  // ---------- CALCULATIONS ----------
  const subtotal = round(billItems.reduce((acc, item) => acc + (item.pricePerUnit * (parseFloat(item.quantity) || 0)), 0));
  const couponDiscountVal = round(subtotal * (couponDiscountPercent / 100));

  const handleDiscountAmt = (val) => {
    const v = Math.min(parseFloat(val) || 0, subtotal - couponDiscountVal);
    setDiscountAmount(v);
    setDiscountPercent(subtotal > 0 ? round((v / subtotal) * 100) : 0);
  };

  const taxPercentVal = parseFloat(taxPercent) || 0;
  const discountVal = (parseFloat(discountAmount) || 0) + couponDiscountVal;
  const taxVal = round((subtotal - discountVal) * (taxPercentVal / 100));
  const grandTotal = round(subtotal - discountVal + taxVal);
  const balance = round((parseFloat(paidAmount) || 0) - grandTotal);

  // ---------- SUBMIT ----------
  const submitSale = async () => {
    if (billItems.length === 0) return toast.error('Cart is empty');

    const hasPrescriptionMed = billItems.some(i => i.prescriptionRequired);
    if (hasPrescriptionMed && !doctorName.trim()) {
      return toast.error('One or more medicines require a prescription. Please enter the prescribing Doctor Name.');
    }

    setLoading(true);
    try {
      const selectedParty = parties.find(p => p._id === selectedPartyId);
      const partyNameStr = selectedParty ? selectedParty.name : (customerName || 'Walk-in Customer');

      const saleData = {
        billNumber,
        party: selectedPartyId || null,
        partyName: partyNameStr,
        items: billItems.map(i => ({
          medicine: i.medicineId,
          name: i.name,
          batchNumber: i.batchNumber,
          batchId: i.batchId,
          quantity: i.quantity,
          quantityUnit: i.quantityUnit,
          price: i.pricePerUnit,
          total: i.pricePerUnit * i.quantity
        })),
        subtotal,
        discount: discountVal,
        taxPercent: taxPercentVal,
        tax: taxVal,
        totalAmount: grandTotal,
        paidAmount: parseFloat(paidAmount) || 0,
        dueAmount: Math.max(0, grandTotal - (parseFloat(paidAmount) || 0)),
        paymentType,
        notes,
        patientName,
        patientAge: parseInt(patientAge) || null,
        patientGender,
        doctorName
      };

      await API.post('/sales', saleData);

      setLastCompletedSale({
        ...saleData,
        createdAt: new Date()
      });

      setShowSuccessModal(true);

      setBillItems([]);
      setPaidAmount('');
      setCustomerName('');
      setSelectedPartyId('');
      setDiscountAmount('');
      setDiscountPercent('');
      setTaxPercent('0');
      setPatientName('');
      setPatientAge('');
      setDoctorName('');
      setNotes('');
      generateBillNumber();
      fetchData();

    } catch (e) {
      console.error(e);
      toast.error('Failed to create sale');
    } finally {
      setLoading(false);
    }
  };

  // ---------- RETURN LOGIC ----------
  const filteredSales = sales.filter(s =>
    (s.billNumber?.toLowerCase().includes(searchBill.toLowerCase()) ||
      s.partyName?.toLowerCase().includes(searchBill.toLowerCase())) &&
    s.paymentType !== 'RETURN'
  ).slice(0, 10);

  const refundTotal = round(returnItems.reduce((a, b) => a + (b.price * (b.returnQty || 0)), 0));

  const processReturn = async () => {
    const items = returnItems.filter(i => i.returnQty > 0);
    if (items.length === 0) return toast.error('No items to return');
    if (!returnReason.trim()) return toast.error('Enter return reason');

    setLoading(true);
    try {
      await API.post('/sales', {
        billNumber: `RET-${selectedSaleForReturn.billNumber}`,
        party: selectedSaleForReturn.party?._id || selectedSaleForReturn.party || null,
        partyName: selectedSaleForReturn.partyName,
        items: items.map(i => ({
          medicine: i.medicine,
          name: i.name,
          batchNumber: i.batchNumber,
          batchId: i.batchId,
          quantity: -i.returnQty,
          price: i.price
        })),
        subtotal: -refundTotal,
        totalAmount: -refundTotal,
        paidAmount: -refundTotal,
        paymentType: 'RETURN',
        notes: `Return: ${returnReason}`
      });
      toast.success('Return Processed');
      setSelectedSaleForReturn(null);
      setReturnReason('');
      setSearchBill('');
      fetchData();
    } catch (e) {
      toast.error('Error processing return');
    } finally {
      setLoading(false);
    }
  };

  // ---------- RESPONSIVE STYLES ----------
  const styles = {
    container: { 
      minHeight: '100vh', 
      background: '#f4f9fd', 
      fontFamily: "'Inter', -apple-system, sans-serif" 
    },
    
    header: { 
      background: '#ffffff', 
      borderBottom: '1px solid #e0f2fe', 
      padding: isMobile ? '12px 16px' : '16px 24px', 
      display: 'flex', 
      flexDirection: isMobile ? 'column' : 'row', 
      justifyContent: 'space-between', 
      alignItems: isMobile ? 'flex-start' : 'center', 
      gap: isMobile ? 12 : 0, 
      position: 'sticky', 
      top: 0, 
      zIndex: 100,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    
    headerTitle: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: isMobile ? 8 : 12,
      width: isMobile ? '100%' : 'auto'
    },
    
    headerIcon: { 
      width: isMobile ? 32 : 40, 
      height: isMobile ? 32 : 40, 
      background: 'linear-gradient(135deg, #1880c2, #0369a1)', 
      borderRadius: 8, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      color: 'white', 
      fontSize: isMobile ? 11 : 13, 
      fontWeight: 700, 
      letterSpacing: '0.5px',
      flexShrink: 0
    },
    
    headerText: { 
      fontSize: isMobile ? 15 : 18, 
      fontWeight: 700, 
      color: '#1a202c', 
      letterSpacing: '-0.3px' 
    },
    
    headerSubtext: { 
      fontSize: isMobile ? 10 : 12, 
      color: '#718096', 
      fontWeight: 500, 
      marginTop: 2,
      display: isMobile ? 'none' : 'block'
    },
    
    headerActions: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: isMobile ? 6 : 10, 
      width: isMobile ? '100%' : 'auto', 
      overflowX: 'auto',
      flexWrap: isMobile ? 'wrap' : 'nowrap'
    },
    
    tabBtn: (active) => ({
      padding: isMobile ? '6px 12px' : '8px 16px',
      background: active ? '#1880c2' : '#ffffff',
      color: active ? 'white' : '#4a5568',
      border: active ? '1px solid #1880c2' : '1px solid #e0f2fe',
      borderRadius: 6, 
      fontSize: isMobile ? 11 : 13, 
      fontWeight: 600, 
      cursor: 'pointer',
      display: 'flex', 
      alignItems: 'center', 
      gap: 4,
      transition: 'all 0.2s', 
      whiteSpace: 'nowrap',
      flex: isMobile ? '1 1 auto' : '0 0 auto'
    }),
    
    main: { 
      maxWidth: isMobile ? '100%' : 1600, 
      margin: '0 auto', 
      padding: isMobile ? '12px' : isTablet ? '16px' : '24px',
      minHeight: 'calc(100vh - 70px)'
    },
    
    grid: { 
      display: 'grid', 
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '1fr 380px', 
      gap: isMobile ? 16 : 20, 
      alignItems: 'start' 
    },
    
    // LEFT COLUMN WRAPPER - KEY FIX FOR SEARCH DROPDOWN
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 12 : 16,
      position: 'relative',
      zIndex: 1,
      overflow: 'visible', // CRITICAL: Allow dropdown to overflow
      minWidth: 0
    },
    
    card: { 
      background: '#ffffff', 
      borderRadius: isMobile ? 8 : 10, 
      border: '1px solid #e0f2fe', 
      marginBottom: isMobile ? 12 : 16, 
      overflow: 'visible', // CHANGED FROM 'hidden' to 'visible'
      boxShadow: '0 1px 2px rgba(24, 128, 194, 0.05)' 
    },
    
    cardHeader: { 
      padding: isMobile ? '12px 16px' : '16px 20px', 
      borderBottom: '1px solid #e0f2fe', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      background: '#f8fafc',
      flexWrap: 'wrap',
      gap: 8
    },
    
    cardTitle: { 
      fontSize: isMobile ? 13 : 15, 
      fontWeight: 700, 
      color: '#1a202c', 
      display: 'flex', 
      alignItems: 'center', 
      gap: 8, 
      margin: 0 
    },
    
    cardBody: { 
      padding: isMobile ? 16 : 20,
      position: 'relative',
      overflow: 'visible' // CRITICAL: Allow dropdown to overflow
    },
    
    input: { 
      width: '100%', 
      padding: isMobile ? '8px 12px' : '10px 14px', 
      borderRadius: 6, 
      border: '1px solid #e1e8ed', 
      fontSize: isMobile ? 13 : 14, 
      boxSizing: 'border-box', 
      outline: 'none', 
      background: '#fff', 
      transition: 'border-color 0.2s' 
    },
    
    select: { 
      width: '100%', 
      padding: isMobile ? '8px 12px' : '10px 14px', 
      borderRadius: 6, 
      border: '1px solid #e1e8ed', 
      fontSize: isMobile ? 13 : 14, 
      boxSizing: 'border-box', 
      outline: 'none', 
      background: 'white', 
      cursor: 'pointer' 
    },
    
    label: { 
      display: 'block', 
      fontSize: isMobile ? 10 : 11, 
      fontWeight: 600, 
      color: '#4a5568', 
      marginBottom: 6, 
      textTransform: 'uppercase', 
      letterSpacing: '0.5px' 
    },
    
    btnPrimary: { 
      padding: isMobile ? '10px 16px' : '10px 20px', 
      background: '#1880c2', 
      color: 'white', 
      border: 'none', 
      borderRadius: 6, 
      fontWeight: 600, 
      cursor: 'pointer', 
      width: '100%', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 6, 
      transition: 'all 0.2s', 
      fontSize: isMobile ? 13 : 14 
    },
    
    btnDanger: { 
      padding: isMobile ? '6px 12px' : '8px 16px', 
      background: '#fef5f5', 
      color: '#991b1b', 
      border: '1px solid #fecaca', 
      borderRadius: 6, 
      fontWeight: 600, 
      cursor: 'pointer', 
      fontSize: isMobile ? 12 : 13, 
      transition: 'all 0.2s' 
    },
    
    btnSecondary: { 
      padding: isMobile ? '8px 16px' : '10px 20px', 
      background: '#ffffff', 
      color: '#4a5568', 
      border: '1px solid #e0f2fe', 
      borderRadius: 6, 
      fontWeight: 600, 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 6, 
      transition: 'all 0.2s', 
      fontSize: isMobile ? 12 : 14 
    },
    
    btnSuccess: { 
      padding: isMobile ? '12px 20px' : '14px 24px', 
      background: '#16a34a', 
      color: 'white', 
      border: 'none', 
      borderRadius: 6, 
      fontWeight: 600, 
      cursor: 'pointer', 
      width: '100%', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 8, 
      transition: 'all 0.2s', 
      fontSize: isMobile ? 13 : 14 
    },
    
    btnPrint: { 
      padding: isMobile ? '8px 16px' : '10px 20px', 
      background: '#1880c2', 
      color: 'white', 
      border: 'none', 
      borderRadius: 6, 
      fontWeight: 600, 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 6, 
      transition: 'all 0.2s', 
      fontSize: isMobile ? 12 : 14 
    },
    
    table: { 
      width: '100%', 
      borderCollapse: 'collapse',
      fontSize: isMobile ? 12 : 13
    },
    
    th: { 
      padding: isMobile ? '10px 12px' : '12px 16px', 
      textAlign: 'left', 
      fontSize: isMobile ? 10 : 11, 
      fontWeight: 600, 
      color: '#718096', 
      background: '#f4f9fd', 
      borderBottom: '1px solid #e0f2fe', 
      textTransform: 'uppercase', 
      letterSpacing: '0.5px' 
    },
    
    td: { 
      padding: isMobile ? '10px 12px' : '12px 16px', 
      fontSize: isMobile ? 12 : 13, 
      color: '#1a202c', 
      borderBottom: '1px solid #f7fafc' 
    },
    
    modal: { 
      position: 'fixed', 
      inset: 0, 
      background: 'rgba(0, 0, 0, 0.5)', 
      backdropFilter: 'blur(4px)', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      zIndex: 9999, 
      padding: isMobile ? 16 : 20,
      overflowY: 'auto'
    },
    
    modalContent: { 
      background: '#ffffff', 
      borderRadius: 12, 
      width: isMobile ? '100%' : '440px', 
      maxWidth: '100%',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)', 
      padding: isMobile ? 20 : 24, 
      maxHeight: isMobile ? '90vh' : '85vh', 
      overflowY: 'auto' 
    },
    
    successModal: { 
      background: '#ffffff', 
      borderRadius: 12, 
      width: isMobile ? '100%' : '450px', 
      maxWidth: '100%',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)', 
      padding: isMobile ? 24 : 32, 
      textAlign: 'center' 
    },

    // FIXED SEARCH CONTAINER STYLES
    searchContainer: {
      position: 'relative',
      zIndex: 2000, // Very high z-index
      isolation: 'isolate' // Creates new stacking context
    },

    // FIXED SEARCH DROPDOWN STYLES
    searchDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 8,
      background: '#ffffff',
      border: '1px solid #e0f2fe',
      borderRadius: 10,
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
      maxHeight: isMobile ? 300 : 400,
      overflowY: 'auto',
      zIndex: 10000, // Extremely high z-index
      isolation: 'isolate' // Creates new stacking context
    }
  };

  const filteredMedicines = medicines.filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 6);

  return (
    <div style={styles.container}>

      {/* QTY & BATCH MODAL */}
      {showQtyModal && selectedMedForQty && (
        <div style={styles.modal} onClick={() => setShowQtyModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', color: '#1a202c', fontSize: isMobile ? 16 : 18, fontWeight: 700 }}>{selectedMedForQty.name}</h3>
            <div style={{ marginBottom: 16, fontSize: isMobile ? 12 : 13, color: '#718096' }}>Select specific batch and enter quantity</div>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Select Batch</label>
              {availableBatches.length > 0 ? (
                <>
                  <select
                    value={selectedBatch ? selectedBatch._id : ''}
                    onChange={e => {
                      const batch = availableBatches.find(b => b._id === e.target.value);
                      setSelectedBatch(batch || null);
                    }}
                    style={styles.select}
                  >
                    {availableBatches.map(b => {
                      const expInfo = getBatchExpiryStatus(b.expiryDate);
                      const tagStr = expInfo ? ` [${expInfo.label}]` : '';
                      return (
                        <option key={b._id} value={b._id}>
                          Batch: {b.batchNumber} (Stock: {b.quantity} | Exp: {new Date(b.expiryDate).toLocaleDateString()}){tagStr} - Rs {calculatePrice(b, selectedMedForQty.category, selectedMedForQty.packaging)}
                        </option>
                      );
                    })}
                  </select>

                  {selectedBatch && getBatchExpiryStatus(selectedBatch.expiryDate) && (
                    <div style={{
                      marginTop: 8,
                      fontSize: isMobile ? 11 : 12,
                      fontWeight: 600,
                      padding: isMobile ? '6px 10px' : '8px 12px',
                      borderRadius: 6,
                      background: getBatchExpiryStatus(selectedBatch.expiryDate).bg,
                      color: getBatchExpiryStatus(selectedBatch.expiryDate).color,
                      border: `1px solid ${getBatchExpiryStatus(selectedBatch.expiryDate).color}40`
                    }}>
                      ⚠️ Notice: Selected batch is {getBatchExpiryStatus(selectedBatch.expiryDate).label}. Please verify before dispensing.
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#991b1b', fontSize: isMobile ? 12 : 13, fontWeight: 600, padding: '8px 12px', background: '#fef5f5', borderRadius: 6, border: '1px solid #fecaca' }}>
                  No active batches in stock. Fallback DEFAULT batch will be used.
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16, fontSize: isMobile ? 12 : 13, color: '#4a5568', display: 'flex', justifyContent: 'space-between', padding: isMobile ? '10px' : '12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e1e8ed' }}>
              <span>Available Batch Stock:</span>
              <strong style={{ color: '#1a202c', fontSize: isMobile ? 13 : 14 }}>{selectedBatch ? selectedBatch.quantity : selectedMedForQty.quantity}</strong>
            </div>

            <div style={{ display: 'flex', gap: isMobile ? 8 : 12, marginBottom: isMobile ? 20 : 24 }}>
              <button onClick={() => setQtyInputValue(p => Math.max(1, p - 1))} style={{ ...styles.btnSecondary, width: isMobile ? '40px' : '48px', padding: 0, fontSize: 20 }}>-</button>
              <input
                type="number"
                ref={qtyInputRef}
                value={qtyInputValue}
                onChange={e => setQtyInputValue(parseInt(e.target.value) || '')}
                style={{ ...styles.input, textAlign: 'center', fontWeight: 700, fontSize: isMobile ? 16 : 18 }}
                onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
              />
              <button onClick={() => setQtyInputValue(p => p + 1)} style={{ ...styles.btnSecondary, width: isMobile ? '40px' : '48px', padding: 0, fontSize: 20 }}>+</button>
            </div>

            <div style={{ display: 'flex', gap: isMobile ? 8 : 12, flexDirection: isMobile ? 'column' : 'row' }}>
              <button onClick={() => setShowQtyModal(false)} style={{ ...styles.btnDanger, flex: 1, padding: isMobile ? '10px 16px' : '12px 16px', fontSize: isMobile ? 13 : 14 }}>Cancel</button>
              <button onClick={confirmAdd} style={{ ...styles.btnPrimary, flex: 1 }}>Add Item</button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && lastCompletedSale && (
        <div style={styles.modal}>
          <div style={styles.successModal}>
            <div style={{ width: isMobile ? 56 : 64, height: isMobile ? 56 : 64, background: '#16a34a', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: isMobile ? 28 : 32, color: 'white' }}>✓</div>
            <h2 style={{ margin: '0 0 8px', color: '#1a202c', fontSize: isMobile ? 18 : 22, fontWeight: 700 }}>Sale Completed</h2>
            <p style={{ margin: '0 0 24px', color: '#718096', fontSize: isMobile ? 13 : 14 }}>Invoice #{lastCompletedSale.billNumber} saved successfully</p>

            <div style={{ background: '#f8fafc', padding: isMobile ? 14 : 16, borderRadius: 8, marginBottom: 24, border: '1px solid #e1e8ed' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#4a5568', fontSize: isMobile ? 13 : 14 }}>Total Amount</span>
                <span style={{ fontWeight: 700, color: '#1880c2', fontSize: isMobile ? 16 : 18 }}>Rs {fmt(lastCompletedSale.totalAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#4a5568', fontSize: isMobile ? 13 : 14 }}>Payment</span>
                <span style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14, color: '#1a202c' }}>{lastCompletedSale.paymentType}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: isMobile ? 8 : 12, flexDirection: isMobile ? 'column' : 'row' }}>
              <button onClick={() => setShowSuccessModal(false)} style={{ ...styles.btnSecondary, flex: 1 }}>Close</button>
              <button onClick={() => { handlePrint(lastCompletedSale); setShowSuccessModal(false); }} style={{ ...styles.btnPrint, flex: 1 }}>
                Print Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HELD INVOICES RESUME MODAL */}
      {showHeldModal && (
        <div style={styles.modal} onClick={() => setShowHeldModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: isMobile ? '100%' : 600, width: isMobile ? '100%' : '90%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0f2fe', paddingBottom: isMobile ? 12 : 16, marginBottom: isMobile ? 12 : 16 }}>
              <h2 style={{ margin: 0, color: '#1a202c', fontSize: isMobile ? 16 : 18, fontWeight: 700 }}>Draft Invoices</h2>
              <button onClick={() => setShowHeldModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer' }}>×</button>
            </div>

            {heldBills.length === 0 ? (
              <div style={{ padding: isMobile ? '30px 20px' : '40px 20px', textAlign: 'center', color: '#718096' }}>
                <div style={{ fontSize: isMobile ? 40 : 48, marginBottom: 16, opacity: 0.3 }}>📄</div>
                <p style={{ fontSize: isMobile ? 13 : 14 }}>No bills currently on hold.</p>
              </div>
            ) : (
              <div style={{ maxHeight: isMobile ? 300 : 350, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 12, marginBottom: isMobile ? 16 : 20, paddingRight: 4 }}>
                {heldBills.map(hb => (
                  <div key={hb.id} style={{ padding: isMobile ? 12 : 16, border: '1px solid #e0f2fe', borderRadius: 8, background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: isMobile ? 10 : 12 }}>
                    <div style={{ flex: '1 1 auto', minWidth: isMobile ? '100%' : 'auto' }}>
                      <div style={{ fontWeight: 700, color: '#1a202c', fontSize: isMobile ? 13 : 14, marginBottom: 4 }}>{hb.tag}</div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#4a5568', marginBottom: 2 }}>{hb.billItems.length} items • Subtotal: Rs {fmt(hb.billItems.reduce((a, b) => a + (b.pricePerUnit * b.quantity), 0))}</div>
                      <div style={{ fontSize: isMobile ? 10 : 11, color: '#94a3b8' }}>{new Date(hb.createdAt).toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                      <button onClick={() => deleteHeldBill(hb.id)} style={{ ...styles.btnDanger, padding: isMobile ? '8px 12px' : '8px 16px', flex: isMobile ? 1 : 'initial' }}>Delete</button>
                      <button onClick={() => resumeBill(hb)} style={{ ...styles.btnPrimary, width: 'auto', padding: isMobile ? '8px 12px' : '8px 16px', flex: isMobile ? 1 : 'initial' }}>Resume</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <div style={styles.headerIcon}>BIL</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.headerText}>Billing System (POS)</div>
            <div style={styles.headerSubtext}>
              Professional Retail POS • <span style={{ color: '#1880c2', fontWeight: 600 }}>Shortcuts: F2 Search | F4 Hold | F8 Pay</span>
            </div>
          </div>
        </div>
        <div style={styles.headerActions}>
          <button onClick={() => setActiveTab('billing')} style={styles.tabBtn(activeTab === 'billing')}>
            {isMobile ? '💳 Sale' : 'New Sale'}
          </button>
          <button onClick={() => setShowHeldModal(true)} style={{ 
            ...styles.tabBtn(false), 
            background: heldBills.length > 0 ? '#fef3c7' : '#ffffff', 
            color: heldBills.length > 0 ? '#d97706' : '#4a5568', 
            borderColor: heldBills.length > 0 ? '#fde68a' : '#e0f2fe' 
          }}>
            {isMobile ? `📋 (${heldBills.length})` : `Held Bills (${heldBills.length})`}
          </button>
          <button onClick={() => setActiveTab('return')} style={styles.tabBtn(activeTab === 'return')}>
            {isMobile ? '↩️ Return' : 'Returns'}
          </button>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main style={styles.main}>
        {activeTab === 'billing' ? (
          <div style={styles.grid}>
            {/* LEFT COLUMN: CUSTOMER & CART - WITH FIXED WRAPPER */}
            <div style={styles.leftColumn}>
              {/* Customer Info Card */}
              <div style={{ ...styles.card, overflow: 'hidden', zIndex: 0 }}>
                <div style={styles.cardHeader}><div style={styles.cardTitle}>👤 Customer & Patient</div></div>
                <div style={{ ...styles.cardBody, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 12 : 16 }}>
                    {/* Customer Selection */}
                    <div>
                      <label style={styles.label}>Registered Party (Optional)</label>
                      <select
                        value={selectedPartyId}
                        onChange={e => {
                          setSelectedPartyId(e.target.value);
                          const partyObj = parties.find(p => p._id === e.target.value);
                          setCustomerName(partyObj ? partyObj.name : '');
                        }}
                        style={styles.select}
                      >
                        <option value="">-- Walk-in Customer --</option>
                        {parties.map(p => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    {/* Custom Customer Name */}
                    <div>
                      <label style={styles.label}>Customer Name</label>
                      <input
                        value={customerName}
                        onChange={e => setCustomerName(e.target.value)}
                        placeholder="Walk-in Customer"
                        style={styles.input}
                        disabled={!!selectedPartyId}
                        onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                        onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                      />
                    </div>
                  </div>

                  {/* Patient Details (ERP Feature) */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr 1fr', gap: isMobile ? 10 : 12, borderTop: '1px dashed #e0f2fe', paddingTop: isMobile ? 12 : 16 }}>
                    <div>
                      <label style={styles.label}>Patient Name</label>
                      <input value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="Enter patient name" style={styles.input} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} />
                    </div>
                    <div>
                      <label style={styles.label}>Age</label>
                      <input type="number" value={patientAge} onChange={e => setPatientAge(e.target.value)} placeholder="Age" style={styles.input} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} />
                    </div>
                    <div>
                      <label style={styles.label}>Gender</label>
                      <select value={patientGender} onChange={e => setPatientGender(e.target.value)} style={styles.select}>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginTop: isMobile ? 10 : 12 }}>
                    <label style={styles.label}>Prescribing Doctor Name</label>
                    <input value={doctorName} onChange={e => setDoctorName(e.target.value)} placeholder="Dr. John Doe (Required for prescription drugs)" style={styles.input} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} />
                  </div>
                </div>
              </div>

              {/* Add Medicines - WITH FIXED SEARCH CONTAINER */}
              <div style={{ ...styles.card, zIndex: 1000 }}> {/* Higher z-index for this card */}
                <div style={styles.cardHeader}>
                  <div style={styles.cardTitle}>
                    <span>🔍 Add Medicines</span>
                    <span style={{ fontSize: isMobile ? 9 : 11, background: '#e0f2fe', color: '#1880c2', padding: '2px 6px', borderRadius: 4, fontWeight: 700, marginLeft: 8 }}>F2</span>
                  </div>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.searchContainer}>
                    <input
                      ref={searchInputRef}
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && filteredMedicines.length > 0) {
                          e.preventDefault();
                          initiateAdd(filteredMedicines[0]);
                        }
                      }}
                      placeholder={isMobile ? "Type medicine name..." : "Type medicine name (Press Enter to add top result, F2 to focus)..."}
                      style={{ ...styles.input, marginBottom: 0 }}
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e1e8ed';
                        // Delay closing dropdown to allow click
                        setTimeout(() => {
                          // Only close if not clicking on dropdown
                          if (!e.relatedTarget || !e.relatedTarget.closest('[data-dropdown="medicines"]')) {
                            // Don't auto-close on mobile to allow better UX
                            if (!isMobile) {
                              // setSearchTerm('');
                            }
                          }
                        }, 200);
                      }}
                    />
                    {searchTerm && (
                      <div style={styles.searchDropdown} data-dropdown="medicines">
                        {filteredMedicines.map(med => (
                          <div 
                            key={med._id} 
                            onClick={() => initiateAdd(med)}
                            onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                            style={{ 
                              padding: isMobile ? '10px 12px' : '12px 16px', 
                              borderBottom: '1px solid #f4f9fd', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              gap: 12,
                              flexWrap: isMobile ? 'wrap' : 'nowrap'
                            }} 
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'} 
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                          >
                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: '#1a202c', fontSize: isMobile ? 13 : 14, wordBreak: 'break-word' }}>{med.name}</div>
                              <div style={{ fontSize: isMobile ? 11 : 12, color: '#718096', marginTop: 2 }}>
                                {isMobile ? `${med.category}` : `Category: ${med.category} | Composition: ${med.composition}`}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontWeight: 700, color: '#1880c2', fontSize: isMobile ? 13 : 14 }}>Rs {fmt(calculatePrice(med, med.category, med.packaging))}</div>
                              <div style={{ fontSize: isMobile ? 10 : 11, color: med.quantity > 0 ? '#16a34a' : '#991b1b', fontWeight: 600, marginTop: 2 }}>
                                Stock: {med.quantity}
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredMedicines.length === 0 && (
                          <div style={{ padding: isMobile ? 16 : 20, textAlign: 'center', color: '#718096', fontSize: isMobile ? 12 : 13 }}>
                            No medicines found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Cart Items List */}
              <div style={{ ...styles.card, overflow: 'hidden', zIndex: 0 }}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardTitle}>🛒 Billing Items</div>
                  <div style={{ background: '#e0f2fe', color: '#1880c2', padding: '4px 10px', borderRadius: 12, fontSize: isMobile ? 10 : 11, fontWeight: 700, border: '1px solid #bae6fd' }}>
                    {billItems.length} items
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  {isMobile ? (
                    // Mobile Card View
                    <div style={{ padding: 16 }}>
                      {billItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                          Cart is empty. Search and add medicines above.
                        </div>
                      ) : (
                        billItems.map(item => (
                          <div key={`${item.medicineId}-${item.batchNumber}`} style={{
                            background: '#f8fafc',
                            border: '1px solid #e0f2fe',
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 12
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#1a202c', fontSize: 13, marginBottom: 4 }}>{item.name}</div>
                                {item.prescriptionRequired && (
                                  <span style={{ fontSize: 9, background: '#fef5f5', color: '#991b1b', padding: '2px 6px', borderRadius: 4, fontWeight: 700, border: '1px solid #fecaca', display: 'inline-block' }}>Rx Required</span>
                                )}
                                <div style={{ fontSize: 11, color: '#4a5568', marginTop: 4 }}>
                                  Batch: <span style={{ fontWeight: 600 }}>{item.batchNumber}</span>
                                </div>
                              </div>
                              <button 
                                onClick={() => removeItem(item.medicineId, item.batchNumber)} 
                                style={{ 
                                  border: 'none', 
                                  background: '#fef5f5', 
                                  color: '#991b1b', 
                                  border: '1px solid #fecaca', 
                                  cursor: 'pointer', 
                                  fontSize: 12, 
                                  width: 24, 
                                  height: 24, 
                                  borderRadius: 6, 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >×</button>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                              <input
                                type="number"
                                min="1"
                                max={item.maxAvailable}
                                value={item.quantity}
                                onChange={e => updateQty(item.medicineId, item.batchNumber, e.target.value)}
                                style={{ 
                                  ...styles.input, 
                                  padding: '6px 8px', 
                                  width: 60, 
                                  textAlign: 'center',
                                  fontSize: 13
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e1e8ed';
                                  if (!item.quantity || item.quantity === '' || item.quantity <= 0) {
                                    updateQty(item.medicineId, item.batchNumber, 1);
                                  }
                                }}
                              />
                              <div style={{ fontSize: 11, color: '#718096' }}>× Rs {fmt(item.pricePerUnit)}</div>
                              <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 14 }}>Rs {fmt(item.pricePerUnit * (parseFloat(item.quantity) || 0))}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    // Desktop Table View
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Item Description</th>
                          <th style={styles.th}>Batch No</th>
                          <th style={styles.th}>Qty</th>
                          <th style={styles.th}>Price</th>
                          <th style={styles.th}>Total</th>
                          <th style={styles.th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {billItems.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                              Cart is empty. Search and add medicines above.
                            </td>
                          </tr>
                        ) : (
                          billItems.map(item => (
                            <tr key={`${item.medicineId}-${item.batchNumber}`}>
                              <td style={styles.td}>
                                <div style={{ fontWeight: 600, color: '#1a202c' }}>{item.name}</div>
                                {item.prescriptionRequired && (
                                  <span style={{ fontSize: 10, background: '#fef5f5', color: '#991b1b', padding: '2px 6px', borderRadius: 4, fontWeight: 700, border: '1px solid #fecaca', display: 'inline-block', marginTop: 4 }}>Rx Required</span>
                                )}
                              </td>
                              <td style={styles.td}>
                                <span style={{ fontSize: 11, background: '#f4f9fd', padding: '4px 8px', borderRadius: 6, fontWeight: 600, color: '#4a5568', border: '1px solid #e0f2fe' }}>{item.batchNumber}</span>
                              </td>
                              <td style={styles.td}>
                                <input
                                  type="number"
                                  min="1"
                                  max={item.maxAvailable}
                                  value={item.quantity}
                                  onChange={e => updateQty(item.medicineId, item.batchNumber, e.target.value)}
                                  style={{ ...styles.input, padding: '6px 8px', width: 70, textAlign: 'center' }}
                                  onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#e1e8ed';
                                    if (!item.quantity || item.quantity === '' || item.quantity <= 0) {
                                      updateQty(item.medicineId, item.batchNumber, 1);
                                    }
                                  }}
                                />
                              </td>
                              <td style={styles.td}>Rs {fmt(item.pricePerUnit)}</td>
                              <td style={{ ...styles.td, fontWeight: 700, color: '#16a34a' }}>Rs {fmt(item.pricePerUnit * (parseFloat(item.quantity) || 0))}</td>
                              <td style={styles.td}>
                                <button onClick={() => removeItem(item.medicineId, item.batchNumber)} style={{ border: 'none', background: '#fef5f5', color: '#991b1b', border: '1px solid #fecaca', cursor: 'pointer', fontSize: 14, width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: BILL SUMMARY */}
            <div>
              <div style={{ 
                ...styles.card, 
                position: isMobile ? 'static' : isTablet ? 'static' : 'sticky', 
                top: isMobile ? 0 : 100,
                marginBottom: 0,
                overflow: 'hidden',
                zIndex: 0
              }}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardTitle}>📄 Invoice Summary</div>
                  {billItems.length > 0 && !isMobile && (
                    <button onClick={handlePrintPreview} style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: 12 }}>
                      Preview
                    </button>
                  )}
                </div>
                <div style={{ ...styles.cardBody, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: isMobile ? 12 : 13 }}>
                    <span style={{ color: '#718096' }}>Invoice #</span>
                    <span style={{ fontWeight: 700, color: '#1a202c', fontSize: isMobile ? 11 : 13 }}>{billNumber}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: isMobile ? 12 : 16, fontSize: isMobile ? 13 : 14 }}>
                    <span style={{ color: '#4a5568' }}>Subtotal</span>
                    <span style={{ fontWeight: 600, color: '#1a202c' }}>Rs {fmt(subtotal)}</span>
                  </div>

                  {/* Discount details */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 12, marginBottom: isMobile ? 12 : 16 }}>
                    <div>
                      <label style={styles.label}>Discount (Rs)</label>
                      <input type="number" value={discountAmount} onChange={e => handleDiscountAmt(e.target.value)} style={styles.input} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} />
                    </div>
                    <div>
                      <label style={styles.label}>Discount %</label>
                      <input
                        type="number"
                        value={discountPercent}
                        onChange={e => {
                          const v = parseFloat(e.target.value) || 0;
                          setDiscountPercent(v);
                          setDiscountAmount(round((subtotal * v) / 100));
                        }}
                        style={styles.input}
                        onFocus={(e) => e.target.style.borderColor = '#1880c2'} 
                        onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                      />
                    </div>
                  </div>

                  {/* Tax selector */}
                  <div style={{ marginBottom: isMobile ? 12 : 16 }}>
                    <label style={styles.label}>VAT/GST Tax Rate</label>
                    <select value={taxPercent} onChange={e => setTaxPercent(e.target.value)} style={styles.select}>
                      <option value="0">0% (Tax Free / Exempt)</option>
                      <option value="5">5% GST</option>
                      <option value="10">10% VAT</option>
                      <option value="13">13% VAT</option>
                      <option value="18">18% GST</option>
                    </select>
                  </div>

                  {/* Promo / Coupon Code Input */}
                  <div style={{ marginBottom: isMobile ? 12 : 16 }}>
                    <label style={styles.label}>Promo / Coupon Code</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="text"
                        placeholder="e.g. WELCOME10"
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value)}
                        disabled={couponApplied}
                        style={{ ...styles.input, flex: 1 }}
                        onFocus={(e) => e.target.style.borderColor = '#1880c2'} 
                        onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                      />
                      {couponApplied ? (
                        <button
                          onClick={() => {
                            setCouponApplied(false);
                            setCouponDiscountPercent(0);
                            setCouponCode('');
                          }}
                          style={{ ...styles.btnDanger, width: 'auto', padding: isMobile ? '8px 12px' : '10px 16px' }}
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          onClick={applyCoupon}
                          style={{ ...styles.btnSecondary, width: 'auto', padding: isMobile ? '8px 12px' : '10px 16px' }}
                        >
                          Apply
                        </button>
                      )}
                    </div>
                    {couponApplied && (
                      <div style={{ fontSize: isMobile ? 10 : 11, color: '#16a34a', fontWeight: 600, marginTop: 6, background: '#f0fdf4', padding: '6px 10px', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                        {couponDiscountPercent}% discount applied!
                      </div>
                    )}
                  </div>

                  {taxVal > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: isMobile ? 12 : 16, fontSize: isMobile ? 12 : 13 }}>
                      <span style={{ color: '#4a5568' }}>Tax Amount:</span>
                      <span style={{ fontWeight: 600, color: '#1a202c' }}>Rs {fmt(taxVal)}</span>
                    </div>
                  )}

                  {/* Grand total highlight */}
                  <div style={{ 
                    background: 'linear-gradient(135deg, #1880c2 0%, #0369a1 100%)', 
                    padding: isMobile ? 16 : 20, 
                    borderRadius: 8, 
                    color: 'white', 
                    textAlign: 'center', 
                    marginBottom: isMobile ? 16 : 20 
                  }}>
                    <div style={{ fontSize: isMobile ? 10 : 11, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>GRAND TOTAL</div>
                    <div style={{ fontSize: isMobile ? 24 : 32, fontWeight: 800 }}>Rs {fmt(grandTotal)}</div>
                  </div>

                  {/* Payment Mode */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: isMobile ? 12 : 16 }}>
                    <button 
                      onClick={() => { setPaymentType('CASH'); setShowQR(false) }} 
                      style={paymentType === 'CASH' ? { ...styles.btnPrimary, flex: 1 } : { ...styles.btnSecondary, flex: 1 }}
                    >
                      💵 Cash
                    </button>
                    <button 
                      onClick={() => { setPaymentType('ONLINE'); setShowQR(true); setPaidAmount(grandTotal) }} 
                      style={paymentType === 'ONLINE' ? { ...styles.btnPrimary, flex: 1 } : { ...styles.btnSecondary, flex: 1 }}
                    >
                      💳 Online
                    </button>
                  </div>

                  {showQR && paymentType === 'ONLINE' && (
                    <div style={{ textAlign: 'center', padding: isMobile ? 12 : 16, border: '1px dashed #e0f2fe', borderRadius: 8, marginBottom: isMobile ? 12 : 16, background: '#f4f9fd' }}>
                      <img 
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=upi://pay?pa=${upiId}&am=${grandTotal}&tn=Bill`} 
                        alt="QR" 
                        style={{ borderRadius: 8, width: isMobile ? 100 : 120, height: isMobile ? 100 : 120 }} 
                      />
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#4a5568', marginTop: 8, fontWeight: 600 }}>Scan to Pay</div>
                    </div>
                  )}

                  <div style={{ marginBottom: isMobile ? 12 : 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                      <label style={{ ...styles.label, marginBottom: 0 }}>Paid Amount</label>
                      {paymentType === 'CASH' && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => setPaidAmount(grandTotal)} style={{ padding: '2px 8px', fontSize: isMobile ? 10 : 11, background: '#f4f9fd', border: '1px solid #e0f2fe', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}>Exact</button>
                          <button type="button" onClick={() => setPaidAmount(100)} style={{ padding: '2px 6px', fontSize: isMobile ? 10 : 11, background: '#f4f9fd', border: '1px solid #e0f2fe', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}>100</button>
                          <button type="button" onClick={() => setPaidAmount(500)} style={{ padding: '2px 6px', fontSize: isMobile ? 10 : 11, background: '#f4f9fd', border: '1px solid #e0f2fe', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}>500</button>
                          <button type="button" onClick={() => setPaidAmount(1000)} style={{ padding: '2px 6px', fontSize: isMobile ? 10 : 11, background: '#f4f9fd', border: '1px solid #e0f2fe', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}>1000</button>
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      value={paidAmount}
                      onChange={e => setPaidAmount(e.target.value)}
                      style={{ ...styles.input, fontSize: isMobile ? 16 : 18, fontWeight: 700 }}
                      disabled={paymentType === 'ONLINE'}
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'} 
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    />
                  </div>

                  {Math.abs(balance) > 0.01 && (
                    <div style={{ 
                      padding: isMobile ? 10 : 12, 
                      background: balance > 0 ? '#f0fdf4' : '#fef5f5', 
                      color: balance > 0 ? '#16a34a' : '#991b1b', 
                      borderRadius: 8, 
                      fontWeight: 700, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: isMobile ? 12 : 16, 
                      border: `1px solid ${balance > 0 ? '#bbf7d0' : '#fecaca'}`, 
                      fontSize: isMobile ? 13 : 14 
                    }}>
                      <span>{balance > 0 ? 'Change' : 'Outstanding Due'}</span>
                      <span>Rs {fmt(Math.abs(balance))}</span>
                    </div>
                  )}

                  <div style={{ marginBottom: isMobile ? 12 : 16 }}>
                    <label style={styles.label}>Billing Notes</label>
                    <textarea 
                      value={notes} 
                      onChange={e => setNotes(e.target.value)} 
                      placeholder="Add invoice remarks..." 
                      style={{ ...styles.input, minHeight: isMobile ? 50 : 60, resize: 'vertical' }} 
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'} 
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} 
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 8 : 12, marginBottom: isMobile ? 8 : 12 }}>
                    <button 
                      onClick={holdBill} 
                      disabled={billItems.length === 0} 
                      style={{ ...styles.btnSecondary, flex: 1 }}
                    >
                      {isMobile ? '⏸️ Hold' : '⏸️ Hold Bill [F4]'}
                    </button>
                    <button 
                      onClick={() => { 
                        if (billItems.length > 0 && window.confirm('Clear cart?')) { 
                          setBillItems([]); 
                          setPaidAmount(''); 
                          setDiscountAmount(''); 
                          setCouponCode(''); 
                          setCouponApplied(false); 
                          setCouponDiscountPercent(0); 
                        } 
                      }} 
                      style={{ ...styles.btnDanger, flex: 1 }}
                    >
                      {isMobile ? '🗑️ Clear' : '🗑️ Clear'}
                    </button>
                  </div>
                  
                  {isMobile && billItems.length > 0 && (
                    <button onClick={handlePrintPreview} style={{ ...styles.btnSecondary, width: '100%', marginBottom: 8 }}>
                      🖨️ Preview Invoice
                    </button>
                  )}

                  <button 
                    onClick={submitSale} 
                    disabled={loading || billItems.length === 0} 
                    style={{ ...styles.btnSuccess, width: '100%', padding: isMobile ? '12px' : '14px', fontSize: isMobile ? 14 : 15 }}
                  >
                    {loading ? '⏳ Processing...' : (isMobile ? '✓ Complete Sale' : '✓ Complete Sale [F8 / Ctrl+Enter]')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ---------- RETURN TAB ---------- */
          <div style={{ maxWidth: isMobile ? '100%' : 1000, margin: '0 auto' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : selectedSaleForReturn ? '1fr 350px' : '1fr',
              gap: isMobile ? 16 : 20 
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16 }}>
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>{selectedSaleForReturn ? '📋 Select Items' : '🔍 Find Invoice'}</div>
                    {selectedSaleForReturn && (
                      <button 
                        onClick={() => { setSelectedSaleForReturn(null); setReturnItems([]) }} 
                        style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: isMobile ? 11 : 12 }}
                      >
                        ← Back
                      </button>
                    )}
                  </div>
                  <div style={styles.cardBody}>
                    {!selectedSaleForReturn ? (
                      <>
                        <input 
                          value={searchBill} 
                          onChange={e => setSearchBill(e.target.value)} 
                          placeholder="Search Invoice No or Customer Name..." 
                          style={{ ...styles.input, marginBottom: isMobile ? 12 : 16 }} 
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'} 
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} 
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {searchBill && filteredSales.map(sale => (
                            <div 
                              key={sale._id} 
                              onClick={() => { setSelectedSaleForReturn(sale); setReturnItems(sale.items.map(i => ({ ...i, returnQty: 0 }))) }}
                              style={{ 
                                padding: isMobile ? 12 : 16, 
                                border: '1px solid #e0f2fe', 
                                borderRadius: 8, 
                                cursor: 'pointer', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                background: '#fff',
                                flexWrap: isMobile ? 'wrap' : 'nowrap',
                                gap: 12
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'} 
                              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                            >
                              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <div style={{ fontWeight: 700, color: '#1a202c', fontSize: isMobile ? 13 : 14, marginBottom: 4 }}>{sale.billNumber}</div>
                                <div style={{ fontSize: isMobile ? 11 : 12, color: '#718096' }}>
                                  {sale.partyName} • {new Date(sale.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                              <div style={{ fontWeight: 700, color: '#16a34a', fontSize: isMobile ? 14 : 16, flexShrink: 0 }}>
                                Rs {fmt(sale.totalAmount)}
                              </div>
                            </div>
                          ))}
                          {searchBill && filteredSales.length === 0 && (
                            <div style={{ textAlign: 'center', padding: isMobile ? 24 : 30, color: '#94a3b8', fontSize: isMobile ? 12 : 13 }}>
                              No bills found
                            </div>
                          )}
                          {!searchBill && (
                            <div style={{ 
                              textAlign: 'center', 
                              padding: isMobile ? '30px 20px' : '40px 20px', 
                              color: '#cbd5e1', 
                              border: '1px dashed #e0f2fe', 
                              borderRadius: 8,
                              fontSize: isMobile ? 12 : 13
                            }}>
                              Enter bill number or customer name to search
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div>
                        {returnItems.map((item, idx) => (
                          <div 
                            key={idx} 
                            style={{ 
                              padding: isMobile ? 12 : 16, 
                              border: '1px solid #e0f2fe', 
                              borderRadius: 8, 
                              marginBottom: isMobile ? 10 : 12, 
                              background: '#f8fafc', 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              flexWrap: 'wrap', 
                              gap: isMobile ? 10 : 12 
                            }}
                          >
                            <div style={{ flex: '1 1 auto', minWidth: isMobile ? '100%' : 'auto' }}>
                              <div style={{ fontWeight: 600, color: '#1a202c', fontSize: isMobile ? 13 : 14, marginBottom: 4 }}>
                                {item.name}
                              </div>
                              <div style={{ fontSize: isMobile ? 11 : 12, color: '#718096' }}>
                                Purchased: {item.quantity} • Rs {fmt(item.price)}/ea
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                              <label style={{ fontSize: isMobile ? 11 : 12, color: '#4a5568', fontWeight: 600 }}>Return Qty:</label>
                              <input 
                                type="number" 
                                min="0" 
                                max={item.quantity} 
                                value={item.returnQty || 0} 
                                onChange={e => {
                                  const v = Math.min(parseInt(e.target.value) || 0, item.quantity);
                                  setReturnItems(prev => prev.map((p, i) => i === idx ? { ...p, returnQty: v } : p));
                                }} 
                                style={{ 
                                  ...styles.input, 
                                  width: isMobile ? 'auto' : 80, 
                                  flex: isMobile ? 1 : 'initial',
                                  padding: '8px', 
                                  textAlign: 'center' 
                                }} 
                                onFocus={(e) => e.target.style.borderColor = '#1880c2'} 
                                onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} 
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedSaleForReturn && (
                <div>
                  <div style={{ 
                    ...styles.card, 
                    position: isMobile ? 'static' : 'sticky', 
                    top: 100, 
                    border: '1px solid #fecaca',
                    marginBottom: 0
                  }}>
                    <div style={{ ...styles.cardHeader, background: '#fef5f5', borderBottom: '1px solid #fecaca' }}>
                      <div style={{ ...styles.cardTitle, color: '#991b1b' }}>💰 Refund Summary</div>
                    </div>
                    <div style={styles.cardBody}>
                      <div style={{ 
                        textAlign: 'center', 
                        marginBottom: isMobile ? 20 : 24, 
                        background: '#fff', 
                        padding: isMobile ? 16 : 20, 
                        borderRadius: 8, 
                        border: '1px solid #e1e8ed' 
                      }}>
                        <div style={{ fontSize: isMobile ? 10 : 11, color: '#718096', fontWeight: 700, letterSpacing: '1px', marginBottom: 8 }}>
                          TOTAL REFUND
                        </div>
                        <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 800, color: '#991b1b' }}>
                          Rs {fmt(refundTotal)}
                        </div>
                      </div>
                      <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                        <label style={styles.label}>Reason for Return</label>
                        <textarea 
                          value={returnReason} 
                          onChange={e => setReturnReason(e.target.value)} 
                          style={{ ...styles.input, minHeight: isMobile ? 70 : 80, resize: 'vertical' }} 
                          placeholder="Reason for return..." 
                          onFocus={(e) => e.target.style.borderColor = '#1880c2'} 
                          onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} 
                        />
                      </div>
                      <button 
                        onClick={processReturn} 
                        disabled={loading || refundTotal === 0} 
                        style={{ 
                          ...styles.btnDanger, 
                          width: '100%', 
                          padding: isMobile ? '12px' : '14px', 
                          fontSize: isMobile ? 14 : 15, 
                          background: '#991b1b', 
                          color: 'white' 
                        }}
                      >
                        {loading ? '⏳ Processing...' : '✓ Confirm Refund'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}